import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuth } from '../components/auth/AuthContext'
import { Button } from '../components/ui/Button'
import { OnboardingLayout } from '../components/layout/OnboardingLayout'
import authService from '../services/authService'

export const OnboardingFinishedPage = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [personType, setPersonType] = useState<string | null>(null)
  const [userType, setUserType] = useState<string | null>(null)
  // A partir de la 2da solicitud del usuario, esta pantalla se alcanza vía
  // el recorrido abreviado (sin stepper) en vez del flujo completo.
  const [isRepeat, setIsRepeat] = useState(false)

  const { logout } = useAuth()
  useEffect(() => {
    authService
      .isCurrentCreditApplicationTheFirst()
      .then(isFirst => setIsRepeat(!isFirst))
      .catch(() => setIsRepeat(false))
  }, [])

  useEffect(() => {
    const getAuthDataFromStorage = () => {
      try {
        const userData = localStorage.getItem('user_data')
        console.log('📦 Raw userData from localStorage:', userData)
        if (userData) {
          const user = JSON.parse(userData)
          console.log('👤 Parsed user object:', user)
          console.log('🏷️ PersonType from user:', user.personType)
          setUserType(user.userType || null)
          return user.personType
        }
        return null
      } catch (error) {
        console.error('Error al obtener datos de autenticación:', error)
        return null
      }
    }

    const pType = getAuthDataFromStorage()
    setPersonType(pType)
  }, [])

  let stepperSteps = [
    { id: '1', title: '', description: '', status: 'completed' as const },
    { id: '2', title: '', description: '', status: 'completed' as const },
    { id: '3', title: '', description: '', status: 'completed' as const },
    { id: '4', title: '', description: '', status: 'completed' as const },
    { id: '5', title: '', description: '', status: 'completed' as const },
    { id: '6', title: '', description: '', status: 'completed' as const },
  ]

  if (personType === 'PFAE') {
    stepperSteps = [
      { id: '1', title: '', description: '', status: 'completed' as const },
      { id: '2', title: '', description: '', status: 'completed' as const },
      { id: '3', title: '', description: '', status: 'completed' as const },
      { id: '4', title: '', description: '', status: 'completed' as const },
      { id: '5', title: '', description: '', status: 'completed' as const },
    ]
  }

  const handleFinalize = async () => {
    if (isLoading) return

    setIsLoading(true)

    if (userType === 'GUARANTOR') {
      try {
        logout()
        navigate('/login')
      } catch (error) {
        console.error('Error in handleFinalize (Guarantor):', error)
      } finally {
        setIsLoading(false)
      }
      return
    }

    const nextUrl = '/onboarding/pending-proposals'

    const finalizePromise = async () => {
      console.log('📍 Starting finalization process...')
      const creditAppId = await authService.getCurrentCreditApplicationId()

      if (!creditAppId) {
        throw new Error('No se encontró el ID de la solicitud')
      }

      console.log('🚀 Finalizing credit application manually...')
      await authService.finalizeCreditApplicationManual(creditAppId)

      console.log('🔄 Updating progressURL...')
      await authService.updateCreditApplicationProgress(creditAppId, nextUrl)

      return 'Solicitud finalizada correctamente'
    }

    try {
      await toast.promise(finalizePromise(), {
        loading: 'Finalizando solicitud...',
        success: msg => msg,
        error: err =>
          `Error: ${err.message || 'No se pudo finalizar la solicitud'}`,
      })

      navigate(nextUrl)
    } catch (error) {
      console.error('❌ Error in handleFinalize:', error)
      // Even if it fails, maybe we still want to navigate?
      // The user said "algunas veces no sé si no le pegue",
      // so showing the error is crucial.
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <OnboardingLayout steps={stepperSteps} showStepper={!isRepeat}>
      <>
        {/* Contenido principal móvil */}
        <div className="md:hidden flex items-center justify-center h-full w-full">
          <div className="text-center px-6 max-w-md mx-auto">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">
              Gracias por completar tu solicitud
            </h1>

            <p className="text-gray-600 mb-8 leading-relaxed">
              Estamos revisando la documentación que proporcionaste y nos
              comunicaremos contigo en las próximas 24 horas, en caso de que tu
              solicitud sea aprobada, para continuar con el proceso.
            </p>

            <Button
              onClick={handleFinalize}
              className="w-full max-w-xs mx-auto"
              variant="outline"
              isLoading={isLoading}
              disabled={isLoading}
            >
              Finalizar
            </Button>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:flex items-center justify-center h-full w-full">
          <div className="text-center max-w-4xl mx-auto px-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">
              Gracias por completar tu solicitud
            </h1>

            <p className="text-xl text-gray-600 mb-12 leading-relaxed max-w-3xl mx-auto">
              Estamos revisando la documentación que proporcionaste y nos
              comunicaremos contigo en las próximas 24 horas, en caso de que tu
              solicitud sea aprobada, para continuar con el proceso.
            </p>

            <Button
              onClick={handleFinalize}
              className="px-12 py-4 text-lg"
              variant="outline"
              isLoading={isLoading}
              disabled={isLoading}
            >
              Finalizar
            </Button>
          </div>
        </div>
      </>
    </OnboardingLayout>
  )
}
